/**
 * Get application version
 *
 * In Docker/production: Uses NEXT_PUBLIC_APP_VERSION from build args
 * In local development: Falls back to reading package.json
 *
 * @param packageJson - Optional package.json object for fallback
 * @returns Version string (e.g., "4.11.0" or "pr-123")
 */
export function getAppVersion(packageJson?: { version: string }): string {
  // In Docker builds, NEXT_PUBLIC_APP_VERSION is set from build args.
  // Start builds can set VITE_APP_VERSION instead.
  if (process.env.NEXT_PUBLIC_APP_VERSION || process.env.VITE_APP_VERSION) {
    return process.env.NEXT_PUBLIC_APP_VERSION ?? process.env.VITE_APP_VERSION ?? 'unknown'
  }

  // Local development fallback: read from package.json
  if (packageJson?.version) {
    return packageJson.version
  }

  // Last resort: return unknown
  return 'unknown'
}

/**
 * Build a metadata title that includes environment/version info for non-production deploys.
 *
 * - Preview: `baseTitle - pr-123`
 * - Dev/staging: `baseTitle - 4.25.7 dev`
 * - Production/local: `baseTitle`
 *
 * @param baseTitle - The default title for the app
 * @param packageJson - Optional package.json for version fallback
 * @param environment - Runtime deployment environment
 * @returns Title string
 */
export function getMetadataTitle(
  baseTitle: string,
  packageJson?: { version: string },
  environment?: string,
): string {
  const version = getAppVersion(packageJson)
  const normalizedEnv = (environment ?? '').trim().toLowerCase()

  if (normalizedEnv === 'preview' || version.startsWith('pr-')) {
    return `${baseTitle} - ${version}`
  }

  if (normalizedEnv === 'develop' || normalizedEnv === 'staging') {
    const label = normalizedEnv === 'develop' ? 'dev' : normalizedEnv
    return `${baseTitle} - ${version} ${label}`
  }

  return baseTitle
}
